/**
 * Stage 5E.2 Phase 3 — Company-side bridge between the legacy
 * CompanyBrandProvider and the new shared BrandProvider.
 *
 * Wires the legacy CompanyBrandProvider (which owns the
 * fetch/ETag/lifecycle) into the shared BrandProvider AND gates
 * the actual Company runtime behind <FirmFirstShell/>.
 *
 * Runtime hierarchy (contract §15, §19, §22):
 *
 *   CompanyBrandProvider           ← legacy: fetch + ETag + loading/error
 *     → CompanyBrandBridge         ← consumes upstream + identity gate
 *       → BrandProvider            ← normalises payload, exposes useBrand()
 *         → FirmFirstShell         ← neutral skeleton until resolved
 *           → children (<Outlet/>) ← real Company shell / pages
 *
 * Identity gate (contract §15 + §22):
 *
 *   The upstream provider exposes BOTH the synchronous current
 *   identity (from props, available on the very first render after
 *   an identity transition) AND the payload identity (set only when
 *   a fetch authoritatively completes for that identity). The bridge
 *   compares the two; any mismatch is treated as ``loading=true`` for
 *   the shared BrandProvider — Company B can NEVER be painted with
 *   Company A's previously-resolved tokens, logo, or shell, even on
 *   the very first commit after the route/identity change.
 *
 *   Until the upstream resolver completes for the *current* identity,
 *   only the neutral skeleton renders — no Outlet, no Ordly header,
 *   no firm logo. After an authoritative firm_first / standard_ordly
 *   response (or a confirmed resolver failure) for the current
 *   identity, the gate opens.
 */
import React from "react";
import { useCompanyBrand } from "../lib/companyBrand";
import { BrandProvider, FirmFirstShell } from "../lib/brand";

function ownerMatches(a, b) {
    if (!a || !b) return false;
    return a.userId === b.userId && a.companyId === b.companyId;
}

export default function CompanyBrandBridge({ children }) {
    const ctx = useCompanyBrand();

    // Identity gate: payload is authoritative ONLY when the identity
    // that owns it equals the current authoritative identity.
    const identityMatches = ownerMatches(
        ctx?.currentOwner,
        ctx?.payloadOwner,
    );
    const effectiveLoading = !!ctx?.loading || !identityMatches;

    // Only forward the payload when its ownership matches the current
    // identity. Otherwise the normaliser sees a null payload and the
    // BrandProvider serves the safe ``ordlyNormalised`` while the
    // skeleton holds — no stale tokens or class can survive the
    // transition.
    const payload = identityMatches ? ctx : null;

    return (
        <BrandProvider
            payload={payload}
            loading={effectiveLoading}
            error={ctx?.error}
            shellFlagOn={ctx?.shellFlagOn}
        >
            <FirmFirstShell>{children}</FirmFirstShell>
        </BrandProvider>
    );
}
